import { useCallback, useRef, useState } from 'react';
import { AnswerFeedback, GamePhase, GameStatus, GameStep, Question } from '../types';
import { shuffleArray } from '../utils/shuffleUtils';
import { playSound } from '../utils/soundEffects';

interface UseGameLogicReturn {
  /** Current state of the game (phase, step, active items) */
  status: GameStatus;
  /** List of all game levels/ladder configuration */
  gamePhases: GamePhase[];
  /** The specific question currently active and displayed */
  gameActualQuestion: Question;
  /** Per-question answer UI state (selection, reveal, 50/50 disabled options) */
  answerFeedback: AnswerFeedback;
  /** True if the game has been won */
  isWin: boolean;
  /** True if the game has been lost */
  isGameOver: boolean;
  setIsWin: (value: boolean) => void;
  setIsGameOver: (value: boolean) => void;
  saveStatus: (status: GameStatus) => void;
  savePhases: (phases: GamePhase[]) => void;
  /** Selects a random question based on current difficulty */
  loadQuestion: (easyQuestions: Question[], mediumQuestions: Question[], hardQuestions: Question[]) => void;
  /** Clears the answer feedback (selection, reveal, 50/50) for a fresh question */
  resetOptions: () => void;
  /** Handler for picking an option (A/B/C/D) - records it as the current selection */
  selectOption: (value: string) => void;
  /** Handler for "Recover/Confirm" - checks answer and transitions state */
  pressResolve: (e: React.MouseEvent<HTMLButtonElement>, actualQuestion: Question) => void;
  /** Handler for "Restart" - wipes progress and starts over */
  pressReset: (e: React.MouseEvent<HTMLButtonElement>, easyQuestions: Question[], mediumQuestions: Question[], hardQuestions: Question[]) => void;
  /** Handler for "Start" - Initializes the first question */
  pressStart: (e: React.MouseEvent<HTMLButtonElement>, easyQuestions: Question[], mediumQuestions: Question[], hardQuestions: Question[]) => void;
  /** Handler for "Next" - Advances to the next question after a correct answer */
  pressNext: (e: React.MouseEvent<HTMLButtonElement>, easyQuestions: Question[], mediumQuestions: Question[], hardQuestions: Question[]) => void;
  renamePhase: (e: React.ChangeEvent<HTMLInputElement>) => void;
  increasePhaseDifficulty: (e: React.MouseEvent<HTMLButtonElement>) => void;
  reducePhaseDifficulty: (e: React.MouseEvent<HTMLButtonElement>) => void;
  increasePhases: (e: React.MouseEvent<HTMLButtonElement>) => void;
  reducePhases: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Handler for using a lifeline (Phone, 50/50, etc) */
  handleHelpItem: (itemIndex: number, easyQuestions?: Question[], mediumQuestions?: Question[], hardQuestions?: Question[]) => void;
}

const INITIAL_QUESTION: Question = {
  id: "",
  pregunta: "",
  "1": "",
  "2": "",
  "3": "",
  "4": "",
  respuesta: "",
  usada: "",
  dificultad: null,
};

const INITIAL_GAME_STATUS: GameStatus = {
  phase: -1,
  question: "",
  step: "start",
  item1: true,
  item2: true,
  item3: true,
  item4: true,
};

const INITIAL_GAME_PHASE: GamePhase = {
  name: "",
  difficulty: 0,
};

const INITIAL_ANSWER_FEEDBACK: AnswerFeedback = {
  selectedValue: null,
  resolved: false,
  isCorrect: false,
  correctValue: "",
  disabledValues: [],
};

// Helper functions
const getQuestionByDifficulty = (
  difficulty: number,
  easy: Question[],
  medium: Question[],
  hard: Question[]
): Question[] => [easy, medium, hard][difficulty] || [];

const getRandomQuestion = (questions: Question[]): Question | null =>
  questions.length > 0 ? questions[Math.floor(Math.random() * questions.length)] : null;

/**
 * Core game logic hook.
 * Manages the state machine (Start -> Resolve -> Next -> Win/Lose).
 * Handles all user interactions and ensures state persistence via LocalStorage.
 * All answer-button visual feedback is plain React state (answerFeedback) rendered
 * declaratively by the components — no DOM manipulation.
 */
export const useGameLogic = (): UseGameLogicReturn => {
  const [status, setStatus] = useState<GameStatus>(() => {
    const stored = localStorage.getItem("status");
    return stored ? JSON.parse(stored) : INITIAL_GAME_STATUS;
  });

  const [isWin, setIsWin] = useState(status.step === "win");
  const [isGameOver, setIsGameOver] = useState(status.step === "lose");


  const [gamePhases, setGamePhases] = useState<GamePhase[]>(() => {
    const stored = localStorage.getItem("phases");
    return stored ? JSON.parse(stored) : [INITIAL_GAME_PHASE];
  });

  const [gameActualQuestion, setGameActualQuestion] = useState<Question>(() => {
    const stored = localStorage.getItem("current_question");
    return stored ? JSON.parse(stored) : INITIAL_QUESTION;
  });

  const [answerFeedback, setAnswerFeedback] = useState<AnswerFeedback>(INITIAL_ANSWER_FEEDBACK);
  // Mirrors answerFeedback.selectedValue for a synchronous read inside pressResolve,
  // the same way `status` is re-read fresh below to avoid acting on a stale closure.
  const selectedValueRef = useRef<string | null>(null);

  const saveCurrentQuestion = useCallback((question: Question) => {
    setGameActualQuestion(question);
    localStorage.setItem("current_question", JSON.stringify(question));
  }, []);

  const saveStatus = useCallback((actualStatus: GameStatus) => {
    setStatus((prevStatus) => {
      // Ensure we are merging correctly if needed, but here we replace the whole status
      return actualStatus;
    });
    localStorage.setItem("status", JSON.stringify(actualStatus));
  }, []);

  const savePhases = useCallback((gamePhasesValues: GamePhase[]) => {
    setGamePhases(gamePhasesValues);
    localStorage.setItem("phases", JSON.stringify(gamePhasesValues));
  }, []);

  const loadQuestion = useCallback((easyQuestions: Question[], mediumQuestions: Question[], hardQuestions: Question[]) => {
    // Read directly from storage to get source of truth
    const currentStatus = JSON.parse(localStorage.getItem("status") || "null") || status;
    const phaseData = gamePhases[currentStatus.phase];

    if (!phaseData) {
      return null;
    }

    const questionPool = getQuestionByDifficulty(phaseData.difficulty, easyQuestions, mediumQuestions, hardQuestions);
    const question = getRandomQuestion(questionPool);

    if (question) {
      saveCurrentQuestion(question);
      return question.id;
    } else {
      return null;
    }
  }, [status, gamePhases, saveCurrentQuestion]);

  const resetOptions = useCallback(() => {
    selectedValueRef.current = null;
    setAnswerFeedback(INITIAL_ANSWER_FEEDBACK);
  }, []);

  const selectOption = useCallback((value: string) => {
    selectedValueRef.current = value;
    setAnswerFeedback((prev) => ({ ...prev, selectedValue: value }));
  }, []);

  /**
   * Main validation logic.
   * Compares the selected option against the correct answer.
   * Updates game status to 'next', 'win', or 'lose'.
   */
  const pressResolve = useCallback((e: React.MouseEvent<HTMLButtonElement>, actualQuestion: Question) => {
    e.preventDefault();

    // Read directly from localStorage to avoid stale React state
    const currentStatus = JSON.parse(localStorage.getItem("status") || "null") || { ...INITIAL_GAME_STATUS };

    if (currentStatus.step !== "resolve") {
      return;
    }

    const selectedValue = selectedValueRef.current;
    if (!selectedValue) {
      return;
    }

    const question = (gameActualQuestion.respuesta === "" && actualQuestion?.respuesta)
      ? actualQuestion
      : gameActualQuestion;

    // Robust comparison: Trim whitespace and ensure string type
    const safeAnswer = selectedValue.trim();
    const safeCorrect = (question.respuesta || "").trim();
    const isCorrect = safeAnswer === safeCorrect;

    const updatedStatus = { ...currentStatus };
    if (isCorrect) {
      playSound('correct');
      updatedStatus.phase++;

      if (updatedStatus.phase >= gamePhases.length) {
        setIsWin(true);
        updatedStatus.step = "win";
      } else {
        updatedStatus.step = "next";
      }
    } else {
      playSound('wrong');
      setIsGameOver(true);
      updatedStatus.step = "lose";
    }

    setAnswerFeedback((prev) => ({ ...prev, resolved: true, isCorrect, correctValue: safeCorrect }));
    saveStatus(updatedStatus);
  }, [gameActualQuestion, gamePhases.length, saveStatus]);

  const pressReset = useCallback((e: React.MouseEvent<HTMLButtonElement>, easyQuestions: Question[], mediumQuestions: Question[], hardQuestions: Question[]) => {
    e.preventDefault();
    Object.keys(localStorage).forEach((key) => {
      if (key !== 'phases' && key !== 'questions') localStorage.removeItem(key);
    });

    const newStatus: GameStatus = { ...INITIAL_GAME_STATUS, step: "start", phase: -1 };

    setGameActualQuestion(INITIAL_QUESTION);
    setIsWin(false);
    setIsGameOver(false);
    resetOptions();
    saveStatus(newStatus);
  }, [resetOptions, saveStatus]);

  /**
   * Initializes a new game session.
   * loads questions, prepares the phase ladder, and sets the first question.
   */
  const pressStart = useCallback((e: React.MouseEvent<HTMLButtonElement>, easyQuestions: Question[], mediumQuestions: Question[], hardQuestions: Question[]) => {
    e.preventDefault();

    const statusData = JSON.parse(localStorage.getItem("status") || JSON.stringify(INITIAL_GAME_STATUS));

    // Use the live phase ladder and the question pools passed in by the caller directly --
    // previously this re-read localStorage("phases")/("questions") instead, which meant a
    // brand-new session (default ladder never yet persisted) silently did nothing at all
    // (see KNOWN_ISSUES.md #3, now fixed).
    const questions = [...easyQuestions, ...mediumQuestions, ...hardQuestions];

    const updatedStatus = { ...statusData, step: "resolve" as GameStep, phase: 0 };

    if (gamePhases.length > 0 && questions.length > 0) {
      const phase_questions = gamePhases.map((phase: GamePhase, index: number) => {
        const filtered = questions.filter((q: Question) => q.dificultad === phase.difficulty.toString());
        const selected = getRandomQuestion(filtered);
        // Only remove the question we actually picked -- previously this spliced
        // unconditionally, and when the pool was empty (selected falls back below)
        // `indexOf` returned -1, silently deleting the LAST item of the whole array
        // (any difficulty) instead of doing nothing (see KNOWN_ISSUES.md #5, now fixed).
        if (selected) {
          questions.splice(questions.indexOf(selected), 1);
        }
        const finalSelected = selected || INITIAL_QUESTION;
        const options = shuffleArray([finalSelected["1"], finalSelected["2"], finalSelected["3"], finalSelected["4"]]);
        return {
          phase_id: index,
          question_id: finalSelected.id,
          // Carry pregunta through directly instead of re-finding it afterward in the
          // now-mutated `questions` array (see KNOWN_ISSUES.md #2, now fixed).
          pregunta: finalSelected.pregunta,
          a: options[0],
          b: options[1],
          c: options[2],
          d: options[3],
          answer: finalSelected.respuesta,
          difficulty: parseInt(finalSelected.dificultad?.toString() || "0"),
        };
      });

      localStorage.setItem("phase_questions", JSON.stringify(phase_questions));
      localStorage.setItem("questions_left", JSON.stringify(questions));

      // Update state without reload
      const firstPhaseQuestion = phase_questions[0];
      if (firstPhaseQuestion) {
        const firstQuestion: Question = {
          id: String(firstPhaseQuestion.question_id),
          pregunta: firstPhaseQuestion.pregunta,
          "1": firstPhaseQuestion.a,
          "2": firstPhaseQuestion.b,
          "3": firstPhaseQuestion.c,
          "4": firstPhaseQuestion.d,
          respuesta: firstPhaseQuestion.answer,
          usada: "",
          dificultad: firstPhaseQuestion.difficulty.toString()
        };
        saveCurrentQuestion(firstQuestion);
        // CRITICAL: Save the question ID in the status so it can be restored on reload
        updatedStatus.question = String(firstPhaseQuestion.question_id);
      }
      saveStatus(updatedStatus);
    }
  }, [saveStatus, saveCurrentQuestion, gamePhases]);

  const pressNext = useCallback((e: React.MouseEvent<HTMLButtonElement>, easyQuestions: Question[], mediumQuestions: Question[], hardQuestions: Question[]) => {
    e.preventDefault();

    const statusData = JSON.parse(localStorage.getItem("status") || "null");
    if (!statusData || statusData.step !== "next") {
      return;
    }

    resetOptions();

    const nextQuestionId = loadQuestion(easyQuestions, mediumQuestions, hardQuestions);

    const updatedStatus = {
      ...statusData,
      step: "resolve" as GameStep,
      question: nextQuestionId || statusData.question
    };

    // CRITICAL: saveStatus updates BOTH the React state (for UI) AND localStorage (for persistence)
    saveStatus(updatedStatus);
  }, [saveStatus, resetOptions, loadQuestion]);

  const renamePhase = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const phaseId = parseInt(e.currentTarget.id.split("-")[1]);
    const updatedPhases = [...gamePhases];
    updatedPhases[phaseId].name = e.currentTarget.value;
    savePhases(updatedPhases);
  }, [gamePhases, savePhases]);

  const updatePhaseDifficulty = useCallback((phaseId: string, increment: number) => {
    const id = parseInt(phaseId);

    // Use state updater function to read current phases, avoiding stale closure
    setGamePhases((currentPhases) => {
      if (id < 0 || id >= currentPhases.length) {
        return currentPhases;
      }

      const currentDifficulty = currentPhases[id].difficulty;
      const newDifficulty = (currentDifficulty + increment + 3) % 3;

      // Only update the target phase, leave all others untouched
      const updatedPhases = currentPhases.map((phase, index) =>
        index === id ? { ...phase, difficulty: newDifficulty } : phase
      );

      // Persist to localStorage
      localStorage.setItem("phases", JSON.stringify(updatedPhases));

      return updatedPhases;
    });
  }, []);

  const increasePhaseDifficulty = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    updatePhaseDifficulty(e.currentTarget.id.split("-")[1], 1);
  }, [updatePhaseDifficulty]);

  const reducePhaseDifficulty = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    updatePhaseDifficulty(e.currentTarget.id.split("-")[1], -1);
  }, [updatePhaseDifficulty]);

  const increasePhases = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    savePhases([...gamePhases, INITIAL_GAME_PHASE]);
  }, [gamePhases, savePhases]);

  const reducePhases = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (gamePhases.length > 1) {
      savePhases(gamePhases.slice(0, -1));
    }
  }, [gamePhases, savePhases]);

  // Help implementations
  const implementFiftyFifty = useCallback(() => {
    playSound('lifeline');
    const question = gameActualQuestion.respuesta ? gameActualQuestion : null;
    if (!question?.respuesta) return;

    const allAnswers = [question["1"], question["2"], question["3"], question["4"]];
    const incorrectIndices = allAnswers
      .map((answer, index) => answer !== question.respuesta ? index : -1)
      .filter(index => index !== -1);

    if (incorrectIndices.length < 2) return;

    const toDisable = incorrectIndices.sort(() => 0.5 - Math.random()).slice(0, 2);
    const disabledValues = toDisable.map((index) => allAnswers[index]);

    setAnswerFeedback((prev) => ({ ...prev, disabledValues }));
  }, [gameActualQuestion]);

  const telephoneHelp = useCallback(() => {
    playSound('lifeline');
    // Logic handled by React state in parent
  }, []);

  const changeQuestion = useCallback(() => {
    playSound('lifeline');
    // Read current status to avoid stale closure
    const currentStatus = JSON.parse(localStorage.getItem("status") || "null") || status;
    if (currentStatus.phase < 0) return;

    const phaseData = gamePhases[currentStatus.phase];
    if (!phaseData) return;

    let question_left = JSON.parse(localStorage.getItem("questions_left") || "null");
    if (!question_left) return;

    let easyQuestions = question_left.filter((question: Question) => question.dificultad === '0');
    let mediumQuestions = question_left.filter((question: Question) => question.dificultad === '1');
    let hardQuestions = question_left.filter((question: Question) => question.dificultad === '2');

    const questionPool = getQuestionByDifficulty(phaseData.difficulty, easyQuestions, mediumQuestions, hardQuestions);
    const availableQuestions = questionPool.filter(q => q.id !== gameActualQuestion.id);
    const newQuestion = getRandomQuestion(availableQuestions);

    if (newQuestion) {
      saveCurrentQuestion(newQuestion);
      saveStatus({ ...currentStatus, question: String(newQuestion.id) });
      resetOptions();
    }
  }, [gamePhases, gameActualQuestion, saveStatus, saveCurrentQuestion, resetOptions, status]);

  const handleHelpItem = useCallback((itemIndex: number, easyQuestions: Question[] = [], mediumQuestions: Question[] = [], hardQuestions: Question[] = []) => {
    const currentStatus = JSON.parse(localStorage.getItem("status") || "null") || status;
    const itemKey = `item${itemIndex + 1}` as keyof GameStatus;
    if (!currentStatus[itemKey]) return;

    const helpers = [telephoneHelp, implementFiftyFifty, () => {}, () => changeQuestion()];
    helpers[itemIndex]?.();

    saveStatus({ ...currentStatus, [itemKey]: false });
  }, [status, saveStatus, implementFiftyFifty, changeQuestion, telephoneHelp]);

  return {
    status,
    gamePhases,
    gameActualQuestion,
    answerFeedback,
    isWin,
    isGameOver,
    setIsWin,
    setIsGameOver,
    saveStatus,
    savePhases,
    loadQuestion,
    resetOptions,
    selectOption,
    pressResolve,
    pressReset,
    pressStart,
    pressNext,
    renamePhase,
    increasePhaseDifficulty,
    reducePhaseDifficulty,
    increasePhases,
    reducePhases,
    handleHelpItem,
  };
};
